import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getCurrentUserSubscription } from '../api/subscription'

export interface Submodule {
  submodule_id: string
  submodule_name: string
  is_enabled: boolean
  granted_at: string
}

export interface Module {
  module_id: string
  module_name: string
  is_active: boolean
  end_date?: string
  submodules: Submodule[]
}

export interface SubscriptionData {
  data: Module[]
  success: boolean
}

interface SubscriptionState {
  subscriptions: Module[]
  isLoading: boolean
  error: string | null
  lastFetched: number | null

  // Actions
  fetchSubscriptions: () => Promise<void>
  setSubscriptions: (data: Module[]) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  clearSubscriptions: () => void

  // Helpers
  isModuleEnabled: (moduleId: string) => boolean
  isModuleActive: (moduleId: string) => boolean
  getModuleName: (moduleId: string) => string | undefined
  getModuleEndDate: (moduleId: string) => string | undefined
  isSubmoduleEnabled: (moduleId: string, submoduleId: string) => boolean
  getEnabledModules: () => Module[]
  getEnabledSubmodules: (moduleId: string) => Submodule[]
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      subscriptions: [],
      isLoading: false,
      error: null,
      lastFetched: null,

      fetchSubscriptions: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await getCurrentUserSubscription()
          if (response.success && response.data) {
            set({ subscriptions: response.data, error: null, lastFetched: Date.now(), isLoading: false })
          } else {
            throw new Error('Failed to load subscriptions')
          }
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load subscriptions', isLoading: false })
        }
      },

      setSubscriptions: (data: Module[]) =>
        set({
          subscriptions: data,
          error: null,
          lastFetched: Date.now()
        }),

      setLoading: (isLoading: boolean) =>
        set({ isLoading }),

      setError: (error: string | null) =>
        set({ error, isLoading: false }),

      clearSubscriptions: () =>
        set({
          subscriptions: [],
          error: null,
          lastFetched: null
        }),

      isModuleEnabled: (moduleId: string) => {
        try {
          const module = get().subscriptions.find(m => m.module_id === moduleId)
          if (!module) {
            console.debug(`[Subscription] Module not found: ${moduleId}`)
            return false
          }
          const hasEnabled = module.submodules.some(s => s.is_enabled)
          console.debug(`[Subscription] Module ${moduleId} enabled: ${hasEnabled}`)
          return hasEnabled
        } catch (error) {
          console.error(`[Subscription] Error checking module ${moduleId}:`, error)
          return false
        }
      },

      isModuleActive: (moduleId: string) => {
        const module = get().subscriptions.find(m => m.module_id === moduleId)
        if (!module) return true // Don't block unknown modules
        return module.is_active
      },

      getModuleName: (moduleId: string) => {
        const module = get().subscriptions.find(m => m.module_id === moduleId)
        return module?.module_name
      },

      getModuleEndDate: (moduleId: string) => {
        const module = get().subscriptions.find(m => m.module_id === moduleId)
        return module?.end_date
      },

      isSubmoduleEnabled: (moduleId: string, submoduleId: string) => {
        try {
          const module = get().subscriptions.find(m => m.module_id === moduleId)
          if (!module) {
            console.debug(`[Subscription] Module not found: ${moduleId}`)
            return false
          }
          const submodule = module.submodules.find(s => s.submodule_id === submoduleId)
          const enabled = submodule?.is_enabled ?? false
          console.debug(`[Subscription] Submodule ${moduleId}/${submoduleId} enabled: ${enabled}`)
          return enabled
        } catch (error) {
          console.error(`[Subscription] Error checking submodule ${moduleId}/${submoduleId}:`, error)
          return false
        }
      },

      getEnabledModules: () => {
        return get().subscriptions.filter(module =>
          module.submodules.some(s => s.is_enabled)
        )
      },

      getEnabledSubmodules: (moduleId: string) => {
        const module = get().subscriptions.find(m => m.module_id === moduleId)
        return module?.submodules.filter(s => s.is_enabled) ?? []
      },
    }),
    {
      name: 'subscription-storage',
      partialize: (state) => ({
        subscriptions: state.subscriptions,
        lastFetched: state.lastFetched
      }),
    }
  )
)
